import type { Accessor, Component } from 'solid-js'
import { Logger } from 'besonders-logger'
import { createSignal, Show } from 'solid-js'
import { getMnemonicFromIDB, initializeCryptoKeypairs } from '../../data/agent/AgentCrypto'
import { DEMO_USERNAME, updateAgentState, useAgent } from '../../data/agent/AgentState'
import { notifyToast, ToastVariant } from '../../ui/utils-ui'
import { Iconify } from '../mini-components'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG) // eslint-disable-line unused-imports/no-unused-vars

interface BasicIdentitySetupProps {
	params?: Accessor<string[]>
	/** Called after the identity has been set up so the page can redirect (mirrors "Skip for now"). */
	onComplete?: () => void
	/** Skip setup and redirect without changing the identity (shared "Skip for now" action). */
	onSkip?: () => void
}

/** Persist a field to both the live agent state and localStorage (same contract as boundInput's onblur). */
function persistAgentField(prop: 'username' | 'devicename', value: string) {
	updateAgentState({ [prop]: value })
	localStorage.setItem('agent.isSetManually', 'true')
	localStorage.setItem(`agent.${prop}`, value)
}

export const BasicIdentitySetup: Component<BasicIdentitySetupProps> = (props) => {
	const agent = useAgent()

	// Start empty when still on the generic demo user so people type a fresh handle.
	const [username, setUsername] = createSignal(agent.username === DEMO_USERNAME ? '' : agent.username)
	const [devicename, setDevicename] = createSignal(agent.devicename)
	const [saving, setSaving] = createSignal(false)

	const canSubmit = () => !saving() && username().trim().length > 0 && devicename().trim().length > 0

	const onSubmit = async () => {
		const newUsername = username().trim()
		const newDevicename = devicename().trim()
		if (!newUsername || !newDevicename) {
			notifyToast('Please enter both a username and a device name', ToastVariant.warning)
			return
		}
		if (newUsername === DEMO_USERNAME) {
			notifyToast(`'${DEMO_USERNAME}' is the generic demo handle — pick your own`, ToastVariant.warning)
			return
		}
		setSaving(true)
		try {
			// The mnemonic is stored keyed by the *current* agent hash; grab it before we change the
			// identity so we can re-derive the keys under the new agent string (same trick AgentEditor uses).
			const originalAg = agent.expectedAgentHash
			const existingMnemonic = await getMnemonicFromIDB(agent, originalAg)
			if (!existingMnemonic) {
				notifyToast('Could not find your key material — try the Advanced tab', ToastVariant.danger)
				throw ERROR('[BasicIdentitySetup] missing mnemonic for current agent', { originalAg })
			}

			// Update the identity fields first so agentStringNoDID (used to derive the keys) reflects them.
			persistAgentField('username', newUsername)
			persistAgentField('devicename', newDevicename)

			const mnemArray = existingMnemonic
				.replaceAll(' ', ',').replaceAll(`\n`, ',').replaceAll(',,,', ',').replaceAll(',,', ',')
				.split(',')
			DEBUG('[BasicIdentitySetup] re-initializing crypto for new identity', { newUsername, newDevicename, ag: agent.ag })
			// Recycle the existing mnemonic, re-derive + re-store the keypairs under the new agent hash and
			// write the agent atoms (ensureAgentAtoms runs inside). skipWarning=true: the ag change is intended.
			await initializeCryptoKeypairs(agent, mnemArray, true)

			notifyToast(`Identity set up as '${newUsername}'`, ToastVariant.success)
			props.onComplete?.()
		} catch (err) {
			ERROR('[BasicIdentitySetup] failed to set up identity', err)
			notifyToast('Failed to set up identity, see console', ToastVariant.danger)
		} finally {
			setSaving(false)
		}
	}

	const onKeydown = (evt: KeyboardEvent) => {
		if (evt.key === 'Enter' && canSubmit()) onSubmit()
	}

	return (
		<div flex='~ col' gap='4' p='4'>
			<div>
				<h2 text='xl' font='semibold' m-0>Set up your identity</h2>
				<p class='text-sm c-gray-600 mt-1 mb-0'>
					Choose a username and a name for this device. This is what other devices will see as the author of the data you create (if you
					choose to share it).
				</p>
			</div>

			<div class='mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2'>
				<wa-input
					label='Username'
					placeholder='e.g. alice'
					value={username()}
					onInput={(e: InputEvent) => setUsername((e.target as HTMLInputElement).value)}
					onKeyDown={onKeydown}
					filled
					autofocus
					spellcheck={false}
					autocomplete='off'
				/>

				<wa-input
					label='Device name'
					placeholder='e.g. laptop'
					value={devicename()}
					onInput={(e: InputEvent) => setDevicename((e.target as HTMLInputElement).value)}
					onKeyDown={onKeydown}
					filled
					spellcheck={false}
					autocomplete='off'
				/>
			</div>

			<div text='xs' class='mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-md bg-gray-100/80 px-3 py-2'>
				<span class='c-gray-500'>Your agent will be</span>
				<code class='c-gray-700 break-all'>
					{username().trim() || DEMO_USERNAME}
					.
					{agent.shortDID}
					@
					{agent.app}
					.
					{agent.agentcode}
					.
					{devicename().trim() || 'device'}
				</code>
			</div>

			<div flex='~ items-center justify-end' gap-3 mt-6>
				<wa-button appearance='plain' size='m' onClick={() => props.onSkip?.()}>
					Skip for now
				</wa-button>
				<wa-button
					variant='success'
					size='m'
					disabled={!canSubmit()}
					onClick={onSubmit}
				>
					<Iconify slot='start' name='floppy-disk' size={4} />
					<Show when={saving()} fallback='Save & continue'>Saving…</Show>
				</wa-button>
			</div>
		</div>
	)
}
